/**
 * Moderation API Service
 *
 * Handles communication with the backend moderation endpoints
 */

import { api } from '../lib/api-client';

const API_BASE = '/moderation';

export interface BanUserRequest {
  userId: string;
  topicId?: string; // null = global ban
  reason: string;
  durationMinutes?: number; // null = permanent
}

export interface MuteUserRequest {
  userId: string;
  topicId?: string;
  reason: string;
  durationMinutes?: number;
}

export interface UserModerationStatus {
  userId: string;
  username: string;
  status: string;
  isBanned: boolean;
  isMuted: boolean;
  mutedUntil?: string;
  banReason?: string;
  muteReason?: string;
  banExpiresAt?: string;
  muteExpiresAt?: string;
  banId?: string;  // ID of the ban record from user_topic_state
  muteId?: string; // ID of the mute record from user_topic_state
}

export interface AuditLog {
  id: string;
  actionType: string;
  moderatorUserId: string;
  moderatorAliasId?: string;
  targetUserId?: string;
  targetAliasId?: string;
  topicId?: string;
  reason: string;
  metadata?: Record<string, any>;
  ipAddress?: string;
  createdAt: string;
}

export interface AuditLogsResponse {
  logs: AuditLog[];
  limit: number;
  offset: number;
}

/**
 * Ban a user from a topic or globally
 * Accepts either UUID or alias - will try by-alias endpoint first
 */
export async function banUser(request: BanUserRequest): Promise<void> {
  // Check if it looks like a UUID (contains dashes or is 32+ chars)
  const isUUID = request.userId.includes('-') || request.userId.length >= 32;

  if (isUUID) {
    // Use UUID endpoint
    await api.post<void>(`/forum${API_BASE}/users/${request.userId}/ban`, {
      reason: request.reason,
      durationMinutes: request.durationMinutes || null,
    });
  } else {
    // Use alias endpoint
    await api.post<void>(`/forum${API_BASE}/users/by-alias/${request.userId}/ban`, {
      reason: request.reason,
      durationMinutes: request.durationMinutes || null,
    });
  }
}

/**
 * Mute a user in a topic or globally
 * Accepts either UUID or alias - will try by-alias endpoint first
 */
export async function muteUser(request: MuteUserRequest): Promise<void> {
  // Check if it looks like a UUID (contains dashes or is 32+ chars)
  const isUUID = request.userId.includes('-') || request.userId.length >= 32;

  if (isUUID) {
    // Use UUID endpoint
    await api.post<void>(`/forum${API_BASE}/users/${request.userId}/mute`, {
      reason: request.reason,
      durationMinutes: request.durationMinutes || null,
    });
  } else {
    // Use alias endpoint
    await api.post<void>(`/forum${API_BASE}/users/by-alias/${request.userId}/mute`, {
      reason: request.reason,
      durationMinutes: request.durationMinutes || null,
    });
  }
}

/**
 * Unban a user
 */
export async function unbanUser(userId: string, topicId?: string, reason?: string): Promise<void> {
  await api.delete<void>(`${API_BASE}/ban/${userId}`, {
    body: JSON.stringify({
      topicId: topicId || null,
      reason: reason || 'Unbanned by moderator',
    }),
  });
}

/**
 * Unmute a user
 */
export async function unmuteUser(userId: string, topicId?: string, reason?: string): Promise<void> {
  await api.delete<void>(`${API_BASE}/mute/${userId}`, {
    body: JSON.stringify({
      topicId: topicId || null,
      reason: reason || 'Unmuted by moderator',
    }),
  });
}

/**
 * Check user's moderation status by username
 * Uses the moderation service endpoint which returns ban/mute IDs
 */
export async function getUserStatus(username: string, topicId?: string): Promise<UserModerationStatus> {
  const params = new URLSearchParams();
  if (topicId) {
    params.set('topicId', topicId);
  }

  return api.get<UserModerationStatus>(`${API_BASE}/status/${username}?${params}`);
}

/**
 * Get audit logs (admin only)
 */
export async function getAuditLogs(limit = 50, offset = 0): Promise<AuditLogsResponse> {
  const params = new URLSearchParams({
    limit: limit.toString(),
    offset: offset.toString(),
  });

  return api.get<AuditLogsResponse>(`${API_BASE}/audit-logs?${params}`);
}

// Appeal types
export interface ModerationAppeal {
  id: string;
  userId: string;
  banId: string;
  appealText: string;
  status: 'pending' | 'approved' | 'rejected';
  reviewedBy?: string;
  reviewNotes?: string;
  createdAt: string;
  reviewedAt?: string;
}

export interface AppealWithDetails {
  appeal: ModerationAppeal;
  username: string;
  banReason: string;
  banType: 'banned' | 'muted';
  topicId?: string;
  reviewerName?: string;
}

export interface CreateAppealRequest {
  banId: string;
  appealText: string;
}

export interface ReviewAppealRequest {
  status: 'approved' | 'rejected';
  reviewNotes: string;
}

/**
 * Create an appeal against a ban/mute
 */
export async function createAppeal(request: CreateAppealRequest): Promise<ModerationAppeal> {
  return api.post<ModerationAppeal>(`${API_BASE}/appeals`, request);
}

/**
 * Get all appeals (admin/moderator only)
 */
export async function getAppeals(status?: 'pending' | 'approved' | 'rejected'): Promise<AppealWithDetails[]> {
  const params = new URLSearchParams();
  if (status) {
    params.set('status', status);
  }

  const url = `${API_BASE}/appeals${params.toString() ? '?' + params.toString() : ''}`;
  return api.get<AppealWithDetails[]>(url);
}

/**
 * Get current user's appeals
 */
export async function getMyAppeals(): Promise<AppealWithDetails[]> {
  return api.get<AppealWithDetails[]>(`${API_BASE}/appeals/me`);
}

/**
 * Review an appeal (approve or reject)
 */
export async function reviewAppeal(appealId: string, request: ReviewAppealRequest): Promise<ModerationAppeal> {
  return api.post<ModerationAppeal>(`${API_BASE}/appeals/${appealId}/review`, request);
}
